<template>
  <div class="settings-container">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link to="/clients" class="breadcrumb-item">
        Clients
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item">{{ client?.name || 'Loading...' }}</span>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Project Overview</span>
    </div>

    <div class="header">
      <h2 class="page-title">{{ client?.name }} - Project Overview</h2>
      <div class="header-actions">
        <router-link :to="`/projects?client=${clientId}`" class="btn btn-primary">
          <i class="fas fa-plus"></i> Create Project
        </router-link>
      </div>
    </div>

    <!-- Configuration Navigation -->
    <div class="config-nav">
      <button :class="['nav-tab', { active: activeTab === 'projects' }]" @click="activeTab = 'projects'">
        <i class="fas fa-project-diagram"></i> Projects & Checks
      </button>
      <button :class="['nav-tab', { active: activeTab === 'available' }]" @click="activeTab = 'available'">
        <i class="fas fa-list-check"></i> Available Checks
      </button>
    </div>

    <!-- Projects & Checks Tab -->
    <div v-if="activeTab === 'projects' && !loading" class="project-settings-content">
      <div class="settings-section">
        <h3>Client Projects & Analysis Configuration</h3>
        <p class="section-description">View and manage projects associated with this client and their configured compliance checks.</p>
        
        <div class="projects-list">
          <div v-for="project in clientProjects" :key="project.id" class="project-overview-card">
            <div class="project-header">
              <div class="project-info">
                <h4>{{ project.name }}</h4>
                <div class="project-meta">
                  <span class="project-type">{{ project.project_type_display || project.project_type }}</span>
                  <span class="project-date">Created {{ formatDate(project.created_at) }}</span>
                  <span class="project-status" :class="project.archived ? 'archived' : 'active'">
                    {{ project.archived ? 'Archived' : 'Active' }}
                  </span>
                </div>
                <p v-if="project.description" class="project-description">{{ project.description }}</p>
              </div>
              <div class="project-actions">
                <router-link :to="`/projects/${project.id}`" class="btn btn-outline-secondary">
                  <i class="fas fa-eye"></i> View Project
                </router-link>
                <router-link :to="`/projects/${project.id}/settings`" class="btn btn-primary">
                  <i class="fas fa-cogs"></i> Project Settings
                </router-link>
              </div>
            </div>
            
            <!-- Project Analysis Summary -->
            <div class="project-analysis-summary">
              <div class="analysis-stats">
                <div class="stat-item">
                  <i class="fas fa-file-alt"></i>
                  <span class="stat-label">Documents</span>
                  <span class="stat-value">{{ project.document_count || 0 }}</span>
                </div>
                <div class="stat-item">
                  <i class="fas fa-check-circle"></i>
                  <span class="stat-label">Completed Analyses</span>
                  <span class="stat-value">{{ project.completed_analyses || 0 }}</span>
                </div>
                <div class="stat-item">
                  <i class="fas fa-list-check"></i>
                  <span class="stat-label">Active Checks</span>
                  <span class="stat-value">{{ project.active_checks || 0 }}</span>
                </div>
              </div>
              
              <!-- Configured Checks Preview -->
              <div v-if="project.configured_checks?.length" class="configured-checks">
                <h5>Configured Compliance Checks:</h5>
                <div class="checks-list">
                  <div v-for="check in project.configured_checks.slice(0, 3)" :key="check.id" class="check-item">
                    <i class="fas fa-check-circle"></i>
                    <span>{{ check.name }}</span>
                  </div>
                  <div v-if="project.configured_checks.length > 3" class="more-checks">
                    +{{ project.configured_checks.length - 3 }} more checks
                  </div>
                </div>
              </div>
              
              <div v-else class="no-checks">
                <i class="fas fa-exclamation-triangle"></i>
                <span>No compliance checks configured yet</span>
                <router-link :to="`/projects/${project.id}/settings`" class="config-link">Configure now</router-link>
              </div>
            </div>
          </div>
          
          <div v-if="!clientProjects?.length" class="empty-projects">
            <i class="fas fa-folder-open"></i>
            <h4>No Projects Found</h4>
            <p>This client doesn't have any projects yet. Create a project to get started with compliance analysis.</p>
            <router-link :to="`/projects?client=${clientId}`" class="btn btn-primary">
              <i class="fas fa-plus"></i> Create First Project
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Checks Tab -->
    <div v-if="activeTab === 'available' && !loading" class="available-checks-content">
      <div class="settings-section">
        <h3>Available Compliance Checks</h3>
        <p class="section-description">View compliance checks that can be configured for this client's projects. Contact your administrator to assign specific checks to projects.</p>
        
        <div class="checks-grid">
          <div v-for="check in availableChecks" :key="check.id" class="check-card">
            <div class="check-header">
              <h4>{{ check.name }}</h4>
              <span class="check-type" :class="check.check_type?.toLowerCase()">
                {{ check.check_type || 'General' }}
              </span>
            </div>
            
            <div class="check-description">
              <p>{{ check.description || 'No description available' }}</p>
            </div>
            
            <div class="check-requirements">
              <h5>Requirements ({{ check.requirements?.length || 0 }})</h5>
              <div v-if="check.requirements?.length" class="requirements-list">
                <div v-for="req in check.requirements.slice(0, 3)" :key="req.id" class="requirement-item">
                  <i class="fas fa-check"></i>
                  <span>{{ req.name }}</span>
                </div>
                <div v-if="check.requirements.length > 3" class="more-requirements">
                  +{{ check.requirements.length - 3 }} more requirements
                </div>
              </div>
              <div v-else class="no-requirements">
                <i class="fas fa-info-circle"></i>
                <span>No specific requirements defined</span>
              </div>
            </div>
            
            <div class="check-usage">
              <div class="usage-stats">
                <span class="usage-label">Used in:</span>
                <span class="usage-count">{{ getCheckUsageCount(check.id) }} project(s)</span>
              </div>
              <div class="check-actions">
                <button class="btn btn-outline-primary" disabled>
                  <i class="fas fa-info-circle"></i> Contact Admin to Assign
                </button>
              </div>
            </div>
          </div>
          
          <div v-if="!availableChecks?.length" class="empty-checks">
            <i class="fas fa-list-check"></i>
            <h4>No Checks Available</h4>
            <p>No compliance checks are currently configured in the system. Contact your administrator to set up compliance checks.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading project information...
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification'
import api from '@/services/api'

export default {
  name: 'ClientWorkflowSettings',
  props: {
    clientId: {
      type: String,
      required: true
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      client: null,
      clientProjects: [],
      availableChecks: [],
      activeTab: 'projects',
      loading: true,
      error: null
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        // Load client info, projects, and available checks
        const [clientResponse, projectsResponse, checksResponse] = await Promise.all([
          api.get(`/clients/${this.clientId}/`),
          api.get(`/projects/?client=${this.clientId}`),
          api.get('/comprehensive-check-definitions/')
        ]);
        
        this.client = clientResponse.data;
        
        // Handle different possible response structures for projects
        let projects = projectsResponse.data;
        if (projects && typeof projects === 'object') {
          if (Array.isArray(projects)) {
            this.clientProjects = projects;
          } else if (projects.results && Array.isArray(projects.results)) {
            this.clientProjects = projects.results;
          } else if (projects.projects && Array.isArray(projects.projects)) {
            this.clientProjects = projects.projects;
          } else {
            this.clientProjects = [];
          }
        } else {
          this.clientProjects = [];
        }
        
        // Handle different possible response structures for checks
        let checks = checksResponse.data;
        if (checks && typeof checks === 'object') {
          if (Array.isArray(checks)) {
            this.availableChecks = checks;
          } else if (checks.results && Array.isArray(checks.results)) {
            this.availableChecks = checks.results;
          } else {
            this.availableChecks = [];
          }
        } else {
          this.availableChecks = [];
        }
        
        // Enhance project data with analysis information
        if (this.clientProjects && Array.isArray(this.clientProjects)) {
          await this.enhanceProjectData();
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to mock data if API fails
        await this.loadMockData();
        this.toast.error('Using demo data - backend connection needed for full functionality');
      } finally {
        this.loading = false;
      }
    },
    
    async enhanceProjectData() {
      // Ensure we have an array to work with
      if (!this.clientProjects || !Array.isArray(this.clientProjects)) {
        console.warn('No projects array to enhance');
        return;
      }
      
      // Enhance each project with analysis statistics
      for (const project of this.clientProjects) {
        if (!project || !project.id) {
          console.warn('Invalid project object:', project);
          continue;
        }
        
        try {
          // Fetch project statistics with better error handling
          const [documentsResponse, analysesResponse, checksResponse] = await Promise.all([
            api.get(`/projects/${project.id}/documents/`).catch(() => ({ data: [] })),
            api.get(`/projects/${project.id}/analysis/`).catch(() => ({ data: [] })),
            api.get(`/projects/${project.id}/available-comprehensive-checks/`).catch(() => ({ data: { available_checks: [] } }))
          ]);
          
          // Handle different response structures
          const documents = Array.isArray(documentsResponse.data) 
            ? documentsResponse.data 
            : (documentsResponse.data?.results || []);
          
          const analyses = Array.isArray(analysesResponse.data) 
            ? analysesResponse.data 
            : (analysesResponse.data?.results || []);
          
          const checksData = checksResponse.data || {};
          const availableChecks = checksData.available_checks || [];
          const currentCheck = checksData.current_selected_check;
          
          project.document_count = documents.length || 0;
          project.completed_analyses = analyses.filter(a => a && a.status === 'COMPLETED').length || 0;
          
          // Set up configured checks based on what's actually configured
          project.configured_checks = [];
          if (currentCheck && currentCheck.id && currentCheck.name) {
            project.configured_checks = [currentCheck];
          }
          project.active_checks = project.configured_checks.length;
          project.available_checks_count = availableChecks.length;
          
        } catch (error) {
          console.error(`Error enhancing project ${project.id}:`, error);
          // Set defaults on error
          project.document_count = 0;
          project.completed_analyses = 0;
          project.active_checks = 0;
          project.configured_checks = [];
          project.available_checks_count = 0;
        }
      }
    },
    
    getCheckUsageCount(checkId) {
      // Count how many projects use this specific check
      if (!this.clientProjects || !Array.isArray(this.clientProjects)) {
        return 0;
      }
      
      return this.clientProjects.filter(project => 
        project && 
        project.configured_checks && 
        Array.isArray(project.configured_checks) &&
        project.configured_checks.some(check => check && check.id === checkId)
      ).length;
    },
    
    async loadMockData() {
      // Fallback mock data for demo purposes
      await this.simulateApiCall();
      
      // Mock client data
      this.client = {
        id: this.clientId,
        name: this.getClientName(this.clientId),
        email: 'contact@client.com'
      };
      
      // Mock projects data
      this.clientProjects = [
        {
          id: 1,
          name: 'Q4 Insurance Review',
          project_type: 'INSURANCE',
          project_type_display: 'Insurance Compliance',
          description: 'Quarterly review of insurance policies and compliance requirements',
          created_at: '2024-01-15T00:00:00Z',
          archived: false,
          document_count: 12,
          completed_analyses: 8,
          active_checks: 1,
          configured_checks: [
            { id: 1, name: 'General Liability Coverage' }
          ],
          default_comprehensive_check: 1,
          default_comprehensive_check_name: 'General Liability Coverage'
        },
        {
          id: 2,
          name: 'Construction Project Alpha',
          project_type: 'CONSTRUCTION',
          project_type_display: 'Construction',
          description: 'Commercial building project compliance tracking',
          created_at: '2024-02-01T00:00:00Z',
          archived: false,
          document_count: 5,
          completed_analyses: 3,
          active_checks: 1,
          configured_checks: [
            { id: 4, name: 'Builder\'s Risk Insurance' }
          ],
          default_comprehensive_check: 4,
          default_comprehensive_check_name: 'Builder\'s Risk Insurance'
        }
      ];
      
      // Mock available checks
      this.availableChecks = [
        {
          id: 1,
          name: 'General Liability Coverage',
          check_type: 'Insurance',
          description: 'Verify general liability insurance coverage meets minimum requirements',
          requirements: [
            { id: 1, name: 'Minimum $1M coverage' },
            { id: 2, name: 'Additional insured clause' },
            { id: 3, name: 'Waiver of subrogation' }
          ]
        },
        {
          id: 2,
          name: 'Workers Compensation',
          check_type: 'Insurance',
          description: 'Validate workers compensation insurance compliance',
          requirements: [
            { id: 4, name: 'State-required coverage' },
            { id: 5, name: 'Current policy status' }
          ]
        },
        {
          id: 3,
          name: 'Professional Liability',
          check_type: 'Insurance',
          description: 'Check professional liability coverage for service providers',
          requirements: [
            { id: 6, name: 'Professional coverage amount' },
            { id: 7, name: 'Errors and omissions coverage' }
          ]
        }
      ];
    },
    
    simulateApiCall() {
      return new Promise(resolve => setTimeout(resolve, 800));
    },
    
    getClientName(clientId) {
      const clientNames = {
        '1': 'ABC Construction LLC',
        '2': 'TechnoGlobal Corp',
        '3': 'MetroHealth Systems',
        '4': 'Summit Manufacturing',
        '5': 'Coastal Logistics'
      };
      return clientNames[clientId] || `Client ${clientId}`;
    },
    

    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    }
  }
}
</script>

<style scoped>
.settings-container {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

/* Project Overview Cards */
.project-overview-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
  transition: box-shadow 0.2s ease;
}

.project-overview-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.project-info h4 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.project-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.project-type {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.project-date {
  color: #6b7280;
  font-size: 0.875rem;
}

.project-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.project-status.active {
  background: #d1fae5;
  color: #065f46;
}

.project-status.completed {
  background: #dbeafe;
  color: #1e40af;
}

.project-status.discovery {
  background: #fef3c7;
  color: #92400e;
}

.project-status.archived {
  background: #f3f4f6;
  color: #6b7280;
}

.project-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.project-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.project-analysis-summary {
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
}

.analysis-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stat-item i {
  color: #6b7280;
  width: 16px;
}

.stat-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.stat-value {
  color: #111827;
  font-weight: 600;
  font-size: 0.875rem;
}

.configured-checks h5 {
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.checks-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.check-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #374151;
}

.check-item i {
  color: #10b981;
  font-size: 0.625rem;
}

.more-checks {
  background: #e5e7eb;
  color: #6b7280;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-style: italic;
}

.no-checks {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f59e0b;
  font-size: 0.875rem;
}

.no-checks i {
  color: #f59e0b;
}

.config-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  margin-left: 8px;
}

.config-link:hover {
  text-decoration: underline;
}

/* Available Checks Grid */
.checks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.check-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s ease;
}

.check-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
}

.check-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.check-header h4 {
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

.check-type {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.check-type.insurance {
  background: #dbeafe;
  color: #1e40af;
}

.check-type.compliance {
  background: #d1fae5;
  color: #065f46;
}

.check-type.general {
  background: #e5e7eb;
  color: #374151;
}

.check-description {
  margin-bottom: 16px;
}

.check-description p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.check-requirements h5 {
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.requirements-list {
  margin-bottom: 12px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.requirement-item i {
  color: #10b981;
  font-size: 0.75rem;
}

.more-requirements {
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
  margin-top: 4px;
}

.no-requirements {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 0.875rem;
}

.check-usage {
  border-top: 1px solid #f3f4f6;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.usage-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.usage-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.usage-count {
  color: #374151;
  font-weight: 600;
  font-size: 0.875rem;
}

.section-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

/* Empty State Styles */
.empty-projects, .empty-checks {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.empty-projects i, .empty-checks i {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 16px;
}

.empty-projects h4, .empty-checks h4 {
  color: #374151;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-projects p, .empty-checks p {
  margin-bottom: 20px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 768px) {
  .checks-grid {
    grid-template-columns: 1fr;
  }
  
  .header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  
  .project-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .project-actions {
    width: 100%;
  }
  
  .analysis-stats {
    gap: 16px;
  }
}

.loading-state {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #374151;
  color: white;
  padding: 12px 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 50;
}

/* Tab Navigation */
.config-nav {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e2e8f0;
}

.nav-tab {
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  border-bottom: 3px solid transparent;
  color: #718096;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-tab:hover {
  color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

.nav-tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
  background: rgba(102, 126, 234, 0.05);
}

/* Project Settings Content */
.project-settings-content {
  padding: 1.5rem 0;
}

.settings-section h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.section-description {
  color: #718096;
  margin-bottom: 2rem;
}

.projects-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.project-setting-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.project-setting-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
}

.project-info h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.project-meta {
  color: #718096;
  font-size: 0.875rem;
  margin: 0;
}

.project-actions .btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.project-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  color: white;
  text-decoration: none;
}

.empty-projects {
  text-align: center;
  padding: 3rem;
  color: #718096;
}

.empty-projects i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-projects p {
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
}

.btn-outline-primary {
  border: 1px solid #667eea;
  color: #667eea;
  background: transparent;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: white;
  text-decoration: none;
}
</style> 